'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Trophy,
  Menu,
  ChevronRight,
  Gavel,
  X,
  Users,
} from 'lucide-react';
import { Logo } from '@/components/common/Logo';

const navigation = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  {
    name: 'Tournaments',
    href: '/dashboard/tournaments',
    icon: Trophy,
    children: [
      { name: 'My Tournaments', href: '/dashboard/tournaments' },
      { name: 'Create Tournament', href: '/dashboard/tournaments/create' },
    ],
  },
  {
    name: 'Teams',
    href: '/dashboard/teams',
    icon: Users,
    children: [
      { name: 'My Teams', href: '/dashboard/teams' },
      // { name: 'Create Team', href: '/dashboard/teams/create' },
    ],
  },
  // {
  //   name: 'Players',
  //   href: '/dashboard/players',
  //   icon: UserCircle,
  //   children: [
  //     { name: 'All Players', href: '/dashboard/players' },
  //     { name: 'Player Assignment', href: '/dashboard/players/assign' },
  //   ],
  // },
  {
    name: 'Auction',
    href: '/dashboard/auction',
    icon: Gavel,
    children: [
      { name: 'Active Auctions', href: '/dashboard/auction' },
      // { name: 'Auction History', href: '/dashboard/auction/history' },
    ],
  },
  // {
  //   name: 'Settings',
  //   href: '/dashboard/settings',
  //   icon: Settings,
  // },
];

export function Sidebar() {
  const pathname = usePathname();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  let hoverTimeout: NodeJS.Timeout;

  const handleMouseEnter = () => {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => setIsCollapsed(false), 100); // Small delay
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      setIsCollapsed(true);
      setExpandedItem(null);
    }, 100);
  };

  return (
    <>
      {/* Mobile menu button - only visible when menu is closed */}
      {!isMobileOpen && (
        <button
          className="fixed left-2 top-4 z-30 rounded-md bg-white p-2 shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl sm:p-2 lg:hidden"
          onClick={() => {
            setIsMobileOpen(true);
            setIsCollapsed(false);
          }}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-primary-600 sm:h-6 sm:w-6" />
        </button>
      )}

      {/* Backdrop - handles closing the menu */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(
          'group fixed inset-y-0 left-0 z-50 flex flex-col border-r border-gray-100/50 bg-white/90 shadow-xl backdrop-blur-sm transition-all duration-500 ease-in-out lg:sticky lg:top-0 lg:min-h-[calc(100vh-4rem)]',
          {
            'w-64 translate-x-0': isMobileOpen,
            '-translate-x-full lg:translate-x-0': !isMobileOpen,
            'w-16 px-0 lg:hover:w-64': isCollapsed,
          }
        )}
      >
        {/* Logo area */}
        <div className="flex h-14 items-center justify-between border-b border-gray-100/50 px-3 sm:h-16 sm:px-4">
          <div
            className={cn('transition-all duration-300', {
              'invisible w-0 opacity-0': isCollapsed,
              'visible opacity-100': !isCollapsed,
            })}
          >
            <Logo size="md" />
          </div>

          {isMobileOpen && (
            <button
              onClick={() => setIsMobileOpen(false)}
              className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-primary-600 lg:hidden"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto sm:space-y-1">
          {navigation.map((item) => {
            const isActive = item.children
              ? pathname.startsWith(item.href)
              : pathname === item.href;
            const isExpanded = expandedItem === item.name;

            return (
              <div key={item.name} className="mb-1 sm:mb-1">
                {item.children ? (
                  <button
                    onClick={() =>
                      setExpandedItem(isExpanded ? null : item.name)
                    }
                    className={cn(
                      'group flex w-full items-center gap-2 rounded-lg px-2 py-2 text-xs font-medium transition-all duration-200 sm:gap-3 sm:px-2 sm:py-2 sm:text-sm',
                      isActive
                        ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50',
                      isCollapsed ? 'justify-center' : 'gap-2 sm:gap-3'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-md transition-all sm:h-9 sm:w-9',
                        isActive
                          ? 'bg-gradient-to-r from-primary-500 to-accent-blue text-white'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span
                      className={cn(
                        'flex-1 whitespace-nowrap text-justify transition-opacity duration-300',
                        isCollapsed ? 'hidden lg:group-hover:block' : 'block'
                      )}
                    >
                      {item.name}
                    </span>
                    {(!isCollapsed || isExpanded) && (
                      <div className="flex w-4 justify-center sm:w-5">
                        <ChevronRight
                          className={cn(
                            'h-3 w-3 text-gray-400 transition-all sm:h-4 sm:w-4',
                            {
                              'rotate-90': isExpanded,
                            }
                          )}
                        />
                      </div>
                    )}
                  </button>
                ) : (
                  <Link
                    href={item.href}
                    onClick={() => setIsMobileOpen(false)}
                    className={cn(
                      'group flex w-full items-center gap-2 rounded-lg px-2 py-2 text-xs font-medium transition-all duration-200 sm:gap-3 sm:px-2 sm:py-2 sm:text-sm',
                      pathname === item.href
                        ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 text-primary-700 shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50',
                      isCollapsed ? 'justify-center' : 'gap-2 sm:gap-3'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-md transition-all sm:h-9 sm:w-9',
                        pathname === item.href
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>
                    <span
                      className={cn(
                        'flex-1 whitespace-nowrap transition-opacity duration-300',
                        isCollapsed ? 'hidden lg:group-hover:block' : 'block'
                      )}
                    >
                      {item.name}
                    </span>
                    {!isCollapsed && <div className="w-4 sm:w-5"></div>}
                  </Link>
                )}
                {/* Submenu */}
                {item.children && (
                  <div
                    className={cn(
                      'mt-1 space-y-1 overflow-hidden pl-8 transition-all duration-300 sm:mt-1 sm:space-y-1 sm:pl-11',
                      isExpanded
                        ? 'max-h-[200px] opacity-100'
                        : 'max-h-0 opacity-0'
                    )}
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        href={child.href}
                        className={cn(
                          'block rounded-lg px-2 py-2 text-xs font-medium transition-all duration-200 sm:px-3 sm:py-2 sm:text-sm',
                          pathname === child.href
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                        )}
                        onClick={() => setIsMobileOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
}
